/**
 * Admin Page Component
 * Administration interface for portal management
 */
import React, { useState, useEffect, useCallback } from "react";
import { Routes, Route, useNavigate, useLocation } from "react-router-dom";
import {
  makeStyles,
  tokens,
  Text,
  Tab,
  TabList,
  Card,
  Button,
  Input,
  Checkbox,
  Dropdown,
  Option,
  Spinner,
  Table,
  TableHeader,
  TableRow,
  TableHeaderCell,
  TableBody,
  TableCell,
  Dialog,
  DialogTrigger,
  DialogSurface,
  DialogTitle,
  DialogBody,
  DialogActions,
  DialogContent,
  Field,
  Badge,
} from "@fluentui/react-components";
import {
  People24Regular,
  Apps24Regular,
  Shield24Regular,
  History24Regular,
  Add24Regular,
  Checkmark24Filled,
  Dismiss24Regular,
  Save24Regular,
  DataUsage24Regular,
} from "@fluentui/react-icons";
import { useMsal } from "@azure/msal-react";
import { useTokenManager } from "../hooks/useTokenManager";

const useStyles = makeStyles({
  container: {
    maxWidth: "1400px",
    margin: "0 auto",
    padding: tokens.spacingHorizontalL,
  },
  header: {
    marginBottom: tokens.spacingVerticalL,
  },
  tabs: {
    marginBottom: tokens.spacingVerticalL,
  },
  content: {
    backgroundColor: tokens.colorNeutralBackground1,
    borderRadius: tokens.borderRadiusMedium,
    padding: tokens.spacingVerticalL,
    minHeight: "400px",
  },
  placeholder: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    height: "300px",
    color: tokens.colorNeutralForeground3,
    textAlign: "center",
  },
  toolbar: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: tokens.spacingVerticalM,
    gap: tokens.spacingHorizontalM,
  },
  searchInput: {
    width: "300px",
  },
  tableContainer: {
    overflowX: "auto",
  },
  checkboxCell: {
    textAlign: "center",
    width: "80px",
    minWidth: "80px",
    maxWidth: "100px",
  },
  toolHeader: {
    textAlign: "center",
    width: "80px",
    minWidth: "80px",
    maxWidth: "100px",
    fontSize: "12px",
    padding: "4px",
  },
  loading: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: tokens.spacingVerticalXXL,
  },
  error: {
    color: tokens.colorPaletteRedForeground1,
    padding: tokens.spacingVerticalM,
  },
  successBadge: {
    backgroundColor: tokens.colorPaletteGreenBackground2,
    color: tokens.colorPaletteGreenForeground2,
  },
  pendingBadge: {
    backgroundColor: tokens.colorPaletteYellowBackground2,
    color: tokens.colorPaletteYellowForeground2,
  },
});

// Types
interface Tool {
  id: string;
  name: string;
  description?: string;
  category?: string;
  metadata?: Record<string, string>;
  available_permissions?: string[];
}

interface User {
  id: string;
  email: string;
  displayName?: string;      // Legacy
  display_name?: string;     // API returns this
  lastSeen?: string;         // Legacy
  last_sync?: string;        // API returns this
  tmd_profile?: string | null;
}

/** TMD profile dropdown options. Keep IDs in sync with crm-service
 *  src/services/tmd_profiles.py and permission-service ALLOWED_TMD_PROFILES.
 *  Empty string represents "no profile" (clears the field). */
const TMD_PROFILE_OPTIONS: { id: string; label: string }[] = [
  { id: "", label: "(none)" },
  { id: "lert", label: "LERT" },
  { id: "existing_customer", label: "Sales Admin" },
  { id: "sales_admin_lert", label: "Sales Admin — LERT" },
  { id: "team_lead", label: "Team Lead" },
  { id: "pm", label: "Project Manager" },
];

interface Permission {
  userId: string;
  userEmail?: string;
  toolId: string;
  grantedAt?: string;
  /** When present, identifies the permission row (e.g. access, finance_user). Legacy rows may omit. */
  permission?: string;
}

function isGranularTool(tool: Tool): boolean {
  return tool.metadata?.permission_grant_mode === "granular";
}

/** Granular perms are paired by the `_user` / `_admin` suffix convention. */
function granularPermsForTool(tool: Tool): { userPerm: string; adminPerm: string } | null {
  const perms = tool.available_permissions || [];
  const userPerm = perms.find((p) => p.endsWith("_user"));
  const adminPerm = perms.find((p) => p.endsWith("_admin"));
  if (!userPerm || !adminPerm) return null;
  return { userPerm, adminPerm };
}

type MatrixColumn =
  | { mode: "simple"; tool: Tool }
  | { mode: "granular"; tool: Tool; perm: string; kind: "user" | "admin"; pair: { userPerm: string; adminPerm: string } };

function buildMatrixColumns(tools: Tool[]): MatrixColumn[] {
  const out: MatrixColumn[] = [];
  for (const t of tools) {
    const pair = isGranularTool(t) ? granularPermsForTool(t) : null;
    if (pair) {
      out.push({ mode: "granular", tool: t, perm: pair.userPerm, kind: "user", pair });
      out.push({ mode: "granular", tool: t, perm: pair.adminPerm, kind: "admin", pair });
    } else {
      out.push({ mode: "simple", tool: t });
    }
  }
  return out;
}

function columnHeader(col: MatrixColumn): string {
  if (col.mode === "simple") {
    return col.tool.name;
  }
  return col.kind === "user" ? `${col.tool.name} (User)` : `${col.tool.name} (Admin)`;
}

import { getConfig } from "../config";

function useFetchApi() {
  const { instance } = useMsal();
  const tokenManager = useTokenManager();

  const fetchApi = useCallback(async <T,>(path: string, options?: RequestInit): Promise<T> => {
    const token = await tokenManager.getToken();
    const { userId, email, roles } = tokenManager.getUserInfo();

    const headers: HeadersInit = {
      "Content-Type": "application/json",
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      "X-User-Id": userId,
      "X-User-Email": email,
      "X-User-Roles": roles.join(","),
      ...options?.headers,
    };

    const apiBase = getConfig().permissionApiUrl || "/api";
    const url = `${apiBase}${path}`;
    let response = await fetch(url, { ...options, headers });

    if (response.status === 401) {
      const freshToken = await tokenManager.getToken(true);
      const retryHeaders: HeadersInit = {
        "Content-Type": "application/json",
        ...(freshToken ? { Authorization: `Bearer ${freshToken}` } : {}),
        "X-User-Id": userId,
        "X-User-Email": email,
        "X-User-Roles": roles.join(","),
        ...options?.headers,
      };
      response = await fetch(url, { ...options, headers: retryHeaders });

      if (response.status === 401) {
        try {
          await instance.acquireTokenRedirect({ scopes: ["openid", "profile", "email"] });
        } catch {}
        throw new Error("Session expired. Redirecting to login...");
      }
    }

    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      throw new Error(error.error?.message || `API error: ${response.status}`);
    }
    return response.json();
  }, [instance, tokenManager]);

  return fetchApi;
}

/**
 * Permission Matrix Tab - Main admin feature
 */
const PermissionsTab: React.FC = () => {
  const styles = useStyles();
  const fetchApi = useFetchApi();
  const [tools, setTools] = useState<Tool[]>([]);
  const [users, setUsers] = useState<User[]>([]);
  const [permissions, setPermissions] = useState<Permission[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  /** Key shapes:
   *  - userId:toolId — simple-tool access (boolean value)
   *  - userId:toolId:permission — granular permission (boolean value)
   *  - tmd_profile:userId — TMD profile selection (string value, "" = clear)
   *  Boolean and string values share the Map; saveChanges branches on the
   *  key prefix to decide which API to call. */
  const [pendingChanges, setPendingChanges] = useState<Map<string, boolean | string>>(new Map());
  const [isSaving, setIsSaving] = useState(false);
  const [showAddUser, setShowAddUser] = useState(false);
  const [newUserEmail, setNewUserEmail] = useState("");
  const [newUserName, setNewUserName] = useState("");

  const matrixColumns = React.useMemo(() => buildMatrixColumns(tools), [tools]);

  const hasPermissionRow = useCallback(
    (userId: string, toolId: string, permission: string): boolean => {
      return permissions.some(
        (p) =>
          p.userId === userId &&
          p.toolId === toolId &&
          (p.permission === permission || (!p.permission && permission === "access")),
      );
    },
    [permissions],
  );

  const resolvePerm = (userId: string, toolId: string, permission: string): boolean => {
    const k3 = `${userId}:${toolId}:${permission}`;
    if (pendingChanges.has(k3)) {
      return pendingChanges.get(k3) as boolean;
    }
    return hasPermissionRow(userId, toolId, permission);
  };

  /** Resolve the displayed tmd_profile for a user (pending → original).
   *  Returns "" when the user has no profile set. */
  const resolveTmdProfile = (user: User): string => {
    const k = `tmd_profile:${user.id}`;
    if (pendingChanges.has(k)) {
      return pendingChanges.get(k) as string;
    }
    return user.tmd_profile || "";
  };

  /** Toggle / set the tmd_profile for a user. Adds a pending change keyed
   *  by tmd_profile:userId; clears the pending change if the selection
   *  matches the user's original value. */
  const setTmdProfile = (user: User, value: string) => {
    const k = `tmd_profile:${user.id}`;
    const original = user.tmd_profile || "";
    if (value === original) {
      setPendingChanges((prev) => {
        const next = new Map(prev);
        next.delete(k);
        return next;
      });
    } else {
      setPendingChanges((prev) => new Map(prev).set(k, value));
    }
  };

  // Load data
  const loadData = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const [toolsRes, usersRes, permsRes] = await Promise.all([
        fetchApi<{ tools: Tool[] }>("/tools"),
        fetchApi<{ users: User[] }>("/users"),
        fetchApi<{ permissions: Permission[] }>("/permissions/all"),
      ]);
      setTools(toolsRes.tools || []);
      setUsers(usersRes.users || []);
      setPermissions(permsRes.permissions || []);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to load data");
    } finally {
      setIsLoading(false);
    }
  }, [fetchApi]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Simple tool: single "access" checkbox (legacy rows without permission count as access)
  const hasSimpleAccess = (userId: string, toolId: string): boolean => {
    const key = `${userId}:${toolId}`;
    if (pendingChanges.has(key)) {
      return pendingChanges.get(key) as boolean;
    }
    return permissions.some(
      (p) =>
        p.userId === userId &&
        p.toolId === toolId &&
        (!p.permission || p.permission === "access"),
    );
  };

  const toggleSimpleAccess = (userId: string, toolId: string) => {
    const key = `${userId}:${toolId}`;
    const current = hasSimpleAccess(userId, toolId);
    const original = permissions.some(
      (p) =>
        p.userId === userId &&
        p.toolId === toolId &&
        (!p.permission || p.permission === "access"),
    );
    if (current === original) {
      setPendingChanges((prev) => new Map(prev).set(key, !current));
    } else {
      setPendingChanges((prev) => {
        const next = new Map(prev);
        next.delete(key);
        return next;
      });
    }
  };

  /** Toggle a granular permission column.
   *  For the "user" column: disabled if the admin perm is on (admin implies user).
   *  For the "admin" column: granting it will auto-revoke the sibling user row
   *  on save (see saveChanges below), so granting admin doesn't leave both rows.
   */
  const toggleGranularColumn = (
    userId: string,
    toolId: string,
    perm: string,
    pair: { userPerm: string; adminPerm: string },
  ) => {
    if (perm === pair.userPerm && resolvePerm(userId, toolId, pair.adminPerm)) {
      return; // admin already covers user; don't let user toggle the now-implied checkbox
    }
    const k = `${userId}:${toolId}:${perm}`;
    const original = hasPermissionRow(userId, toolId, perm);
    const current = resolvePerm(userId, toolId, perm);
    if (current === original) {
      setPendingChanges((prev) => new Map(prev).set(k, !current));
    } else {
      setPendingChanges((prev) => {
        const next = new Map(prev);
        next.delete(k);
        return next;
      });
    }
  };

  // Save changes
  const saveChanges = useCallback(async () => {
    setIsSaving(true);
    setError(null);
    try {
      // For granular tools the "access" permission gates the home-page tile
      // (see useToolRegistry.ts). Admins toggle only the _user / _admin
      // sub-permissions; "access" is implied. Augment pending changes so any
      // granted sub-permission also grants "access", and revoking the last
      // sub-permission also revokes "access".
      const augmented = new Map<string, boolean | string>(pendingChanges);
      for (const tool of tools) {
        if (!isGranularTool(tool)) continue;
        const pair = granularPermsForTool(tool);
        if (!pair) continue;
        const affectedUsers = new Set<string>();
        for (const k of pendingChanges.keys()) {
          if (k.startsWith("tmd_profile:")) continue;
          const parts = k.split(":");
          if (parts.length === 3 && parts[1] === tool.id &&
              (parts[2] === pair.userPerm || parts[2] === pair.adminPerm)) {
            affectedUsers.add(parts[0]);
          }
        }
        for (const userId of affectedUsers) {
          const userKey = `${userId}:${tool.id}:${pair.userPerm}`;
          const adminKey = `${userId}:${tool.id}:${pair.adminPerm}`;
          const finalUser = pendingChanges.has(userKey)
            ? (pendingChanges.get(userKey) as boolean)
            : hasPermissionRow(userId, tool.id, pair.userPerm);
          const finalAdmin = pendingChanges.has(adminKey)
            ? (pendingChanges.get(adminKey) as boolean)
            : hasPermissionRow(userId, tool.id, pair.adminPerm);
          const wantAccess = finalUser || finalAdmin;
          const hadAccess = hasPermissionRow(userId, tool.id, "access");
          const accessKey = `${userId}:${tool.id}:access`;
          if (wantAccess !== hadAccess && !augmented.has(accessKey)) {
            augmented.set(accessKey, wantAccess);
          }
        }
      }
      for (const [key, desired] of augmented) {
        // TMD profile updates use a tmd_profile:userId key with a string
        // value (profile_id or "" to clear). Handle these before the
        // boolean permission paths below.
        if (key.startsWith("tmd_profile:")) {
          const userId = key.slice("tmd_profile:".length);
          await fetchApi(`/users/${encodeURIComponent(userId)}/tmd-profile`, {
            method: "PUT",
            body: JSON.stringify({ tmd_profile: (desired as string) || null }),
          });
          continue;
        }
        const parts = key.split(":");
        if (parts.length === 3) {
          const [userId, toolId, permission] = parts;
          if (desired) {
            await fetchApi("/permissions/grant", {
              method: "POST",
              body: JSON.stringify({
                user_id: userId,
                tool_id: toolId,
                permission,
              }),
            });
            // Admin perm implies user perm, so auto-revoke the sibling _user
            // row when granting the _admin perm. Works for any granular tool
            // following the _user / _admin suffix convention.
            if (permission.endsWith("_admin")) {
              const siblingUserPerm = permission.replace(/_admin$/, "_user");
              try {
                await fetchApi("/permissions/revoke", {
                  method: "DELETE",
                  body: JSON.stringify({
                    user_id: userId,
                    tool_id: toolId,
                    permission: siblingUserPerm,
                  }),
                });
              } catch {
                /* no separate _user row, or revoke failed harmlessly */
              }
            }
          } else {
            await fetchApi("/permissions/revoke", {
              method: "DELETE",
              body: JSON.stringify({
                user_id: userId,
                tool_id: toolId,
                permission,
              }),
            });
          }
        } else if (parts.length === 2) {
          const [userId, toolId] = parts;
          await fetchApi(
            `/permissions/tool-access?user_id=${encodeURIComponent(userId)}&tool_id=${encodeURIComponent(toolId)}&has_access=${desired}`,
            { method: "POST" },
          );
        }
      }
      setPendingChanges(new Map());
      await loadData();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to save changes");
    } finally {
      setIsSaving(false);
    }
  }, [fetchApi, pendingChanges, loadData, tools, hasPermissionRow]);

  // Add new user
  const handleAddUser = useCallback(async () => {
    if (!newUserEmail) return;
    try {
      const userId = `manual-${Date.now()}`; // Temporary ID until they log in
      await fetchApi("/users/sync", {
        method: "POST",
        body: JSON.stringify({
          id: userId,
          email: newUserEmail,
          display_name: newUserName || newUserEmail.split("@")[0],
        }),
      });
      setShowAddUser(false);
      setNewUserEmail("");
      setNewUserName("");
      await loadData();
    } catch (err) {
      const message = err instanceof Error ? err.message : "Failed to add user";
      // Handle specific error cases with friendly messages
      if (message.includes("409") || message.toLowerCase().includes("already exists")) {
        setError(`User with email "${newUserEmail}" already exists.`);
      } else if (message.includes("400") || message.includes("USER_NOT_FOUND_IN_ENTRA") || message.includes("not found in") || message.includes("does not exist")) {
        setError(`No user with email "${newUserEmail}" exists in the LensLock directory. Please verify the email address is correct.`);
      } else if (message.includes("503") || message.includes("SERVICE_UNAVAILABLE")) {
        setError("Unable to verify user in directory. Please try again later or contact an administrator.");
      } else {
        setError(message);
      }
    }
  }, [fetchApi, newUserEmail, newUserName, loadData]);

  if (isLoading) {
    return (
      <div className={styles.loading}>
        <Spinner size="large" label="Loading permission data..." />
      </div>
    );
  }

  return (
    <div>
      {/* Toolbar */}
      <div className={styles.toolbar}>
        <div style={{ display: "flex", gap: tokens.spacingHorizontalM, alignItems: "center" }}>
          <Text size={500} weight="semibold">
            Permission Matrix
          </Text>
          {pendingChanges.size > 0 && (
            <Badge className={styles.pendingBadge}>
              {pendingChanges.size} unsaved changes
            </Badge>
          )}
        </div>
        <div style={{ display: "flex", gap: tokens.spacingHorizontalS }}>
          <Dialog open={showAddUser} onOpenChange={(_, data) => {
            setShowAddUser(data.open);
            if (data.open) {
              // Clear form when opening
              setNewUserEmail("");
              setNewUserName("");
              setError(null);
            }
          }}>
            <DialogTrigger disableButtonEnhancement>
              <Button icon={<Add24Regular />}>Add User</Button>
            </DialogTrigger>
            <DialogSurface>
              <DialogBody>
                <DialogTitle>Add User</DialogTitle>
                <DialogContent>
                  <Field label="Email" required>
                    <Input
                      value={newUserEmail}
                      onChange={(_, data) => setNewUserEmail(data.value)}
                      placeholder="user@lenslock.com"
                    />
                  </Field>
                  <Field label="Display Name" style={{ marginTop: tokens.spacingVerticalM }}>
                    <Input
                      value={newUserName}
                      onChange={(_, data) => setNewUserName(data.value)}
                      placeholder="John Doe"
                    />
                  </Field>
                </DialogContent>
                <DialogActions>
                  <Button appearance="secondary" onClick={() => setShowAddUser(false)}>
                    Cancel
                  </Button>
                  <Button appearance="primary" onClick={handleAddUser}>
                    Add User
                  </Button>
                </DialogActions>
              </DialogBody>
            </DialogSurface>
          </Dialog>
          <Button
            appearance="primary"
            icon={<Save24Regular />}
            onClick={saveChanges}
            disabled={pendingChanges.size === 0 || isSaving}
          >
            {isSaving ? "Saving..." : "Save Changes"}
          </Button>
        </div>
      </div>

      {error && <div className={styles.error}>{error}</div>}

      {/* Matrix Table */}
      <div className={styles.tableContainer}>
        {users.length === 0 ? (
          <div className={styles.placeholder}>
            <People24Regular style={{ fontSize: 48, marginBottom: 16 }} />
            <Text size={500} weight="semibold">No users yet</Text>
            <Text>Add users above to manage their tool access</Text>
          </div>
        ) : tools.length === 0 ? (
          <div className={styles.placeholder}>
            <Apps24Regular style={{ fontSize: 48, marginBottom: 16 }} />
            <Text size={500} weight="semibold">No tools configured</Text>
            <Text>Tools will appear here once configured</Text>
          </div>
        ) : (
          <Table size="small">
            <TableHeader>
              <TableRow>
                <TableHeaderCell style={{ minWidth: 180 }}>User</TableHeaderCell>
                <TableHeaderCell style={{ width: 140, minWidth: 140, maxWidth: 140 }}>TMD Profile</TableHeaderCell>
                {matrixColumns.map((col) => (
                  <TableHeaderCell
                    key={
                      col.mode === "simple"
                        ? col.tool.id
                        : `${col.tool.id}-${col.perm}`
                    }
                    className={styles.toolHeader}
                  >
                    {columnHeader(col)}
                  </TableHeaderCell>
                ))}
              </TableRow>
            </TableHeader>
            <TableBody>
              {users.map((user) => {
                const profileValue = resolveTmdProfile(user);
                const profilePending = pendingChanges.has(`tmd_profile:${user.id}`);
                const profileLabel =
                  TMD_PROFILE_OPTIONS.find((o) => o.id === profileValue)?.label
                  ?? "(none)";
                return (
                <TableRow key={user.id}>
                  <TableCell>
                    <div>
                      <Text weight="semibold" size={200}>{user.display_name || user.displayName || user.email}</Text>
                      <br />
                      <Text size={100} style={{ color: tokens.colorNeutralForeground3 }}>
                        {user.email}
                      </Text>
                    </div>
                  </TableCell>
                  <TableCell style={{ width: 140, minWidth: 140, maxWidth: 140, overflow: "hidden" }}>
                    <Dropdown
                      size="small"
                      value={profileLabel}
                      selectedOptions={[profileValue]}
                      onOptionSelect={(_, data) => {
                        if (data.optionValue !== undefined) {
                          setTmdProfile(user, data.optionValue);
                        }
                      }}
                      style={{
                        minWidth: 0,
                        width: "100%",
                        ...(profilePending ? { outline: "2px solid orange", borderRadius: 4 } : {}),
                      }}
                    >
                      {TMD_PROFILE_OPTIONS.map((opt) => (
                        <Option key={opt.id || "none"} value={opt.id} text={opt.label}>
                          {opt.label}
                        </Option>
                      ))}
                    </Dropdown>
                  </TableCell>
                  {matrixColumns.map((col) => {
                    if (col.mode === "simple") {
                      const tool = col.tool;
                      const key = `${user.id}:${tool.id}`;
                      const checked = hasSimpleAccess(user.id, tool.id);
                      const isPending = pendingChanges.has(key);
                      return (
                        <TableCell key={tool.id} className={styles.checkboxCell}>
                          <Checkbox
                            checked={checked}
                            onChange={() => toggleSimpleAccess(user.id, tool.id)}
                            style={isPending ? { outline: "2px solid orange", borderRadius: 4 } : undefined}
                          />
                        </TableCell>
                      );
                    }
                    const { tool, perm, kind, pair } = col;
                    const pk = `${user.id}:${tool.id}:${perm}`;
                    const isPending = pendingChanges.has(pk);
                    const adminOn = resolvePerm(user.id, tool.id, pair.adminPerm);
                    const userOnly = resolvePerm(user.id, tool.id, pair.userPerm);
                    // User column shows checked when admin is on (admin implies user)
                    // but is disabled so toggling happens via the admin column.
                    const checked = kind === "user" ? userOnly || adminOn : adminOn;
                    const disabled = kind === "user" && adminOn;
                    return (
                      <TableCell key={pk} className={styles.checkboxCell}>
                        <Checkbox
                          checked={checked}
                          disabled={disabled}
                          onChange={() => toggleGranularColumn(user.id, tool.id, perm, pair)}
                          style={isPending ? { outline: "2px solid orange", borderRadius: 4 } : undefined}
                        />
                      </TableCell>
                    );
                  })}
                </TableRow>
                );
              })}
            </TableBody>
          </Table>
        )}
      </div>
    </div>
  );
};

/**
 * Users Management Tab
 */
const UsersTab: React.FC = () => {
  const styles = useStyles();
  const fetchApi = useFetchApi();
  const [users, setUsers] = useState<User[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showAddUser, setShowAddUser] = useState(false);
  const [newUserEmail, setNewUserEmail] = useState("");
  const [newUserName, setNewUserName] = useState("");
  const [error, setError] = useState<string | null>(null);

  const loadUsers = useCallback(() => {
    fetchApi<{ users: User[] }>("/users")
      .then((res) => setUsers(res.users || []))
      .catch((err) => setError(err instanceof Error ? err.message : "Failed to load users"))
      .finally(() => setIsLoading(false));
  }, [fetchApi]);

  useEffect(() => {
    loadUsers();
  }, [loadUsers]);

  const handleAddUser = async () => {
    if (!newUserEmail) return;
    setError(null);
    try {
      const userId = `user-${Date.now()}`;
      await fetchApi("/users/sync", {
        method: "POST",
        body: JSON.stringify({
          id: userId,
          email: newUserEmail,
          display_name: newUserName || newUserEmail.split("@")[0],
        }),
      });
      setShowAddUser(false);
      setNewUserEmail("");
      setNewUserName("");
      loadUsers();
    } catch (err) {
      const message = err instanceof Error ? err.message : "Failed to add user";
      // Handle specific error cases with friendly messages
      if (message.includes("409") || message.toLowerCase().includes("already exists")) {
        setError(`User with email "${newUserEmail}" already exists.`);
      } else if (message.includes("400") || message.includes("USER_NOT_FOUND_IN_ENTRA") || message.includes("not found in") || message.includes("does not exist")) {
        setError(`No user with email "${newUserEmail}" exists in the LensLock directory. Please verify the email address is correct.`);
      } else if (message.includes("503") || message.includes("SERVICE_UNAVAILABLE")) {
        setError("Unable to verify user in directory. Please try again later or contact an administrator.");
      } else {
        setError(message);
      }
    }
  };

  if (isLoading) {
    return (
      <div className={styles.loading}>
        <Spinner size="large" />
      </div>
    );
  }

  return (
    <div>
      <div className={styles.toolbar}>
        <Text size={500} weight="semibold">
          Portal Users ({users.length})
        </Text>
        <Dialog open={showAddUser} onOpenChange={(_, data) => {
          setShowAddUser(data.open);
          if (data.open) {
            // Clear form when opening
            setNewUserEmail("");
            setNewUserName("");
            setError(null);
          }
        }}>
          <DialogTrigger disableButtonEnhancement>
            <Button appearance="primary" icon={<Add24Regular />}>Add User</Button>
          </DialogTrigger>
          <DialogSurface>
            <DialogBody>
              <DialogTitle>Add User</DialogTitle>
              <DialogContent>
                <Field label="Email" required>
                  <Input
                    value={newUserEmail}
                    onChange={(_, data) => setNewUserEmail(data.value)}
                    placeholder="user@lenslock.com"
                  />
                </Field>
                <Field label="Display Name" style={{ marginTop: tokens.spacingVerticalM }}>
                  <Input
                    value={newUserName}
                    onChange={(_, data) => setNewUserName(data.value)}
                    placeholder="John Doe"
                  />
                </Field>
                {error && <div className={styles.error}>{error}</div>}
              </DialogContent>
              <DialogActions>
                <Button appearance="secondary" onClick={() => setShowAddUser(false)}>
                  Cancel
                </Button>
                <Button appearance="primary" onClick={handleAddUser}>
                  Add User
                </Button>
              </DialogActions>
            </DialogBody>
          </DialogSurface>
        </Dialog>
      </div>
      {users.length === 0 ? (
        <div className={styles.placeholder}>
          <People24Regular style={{ fontSize: 48, marginBottom: 16 }} />
          <Text>No users have been added yet</Text>
          <Text size={200} style={{ marginTop: 8 }}>Click "Add User" to get started</Text>
        </div>
      ) : (
        <Table>
          <TableHeader>
            <TableRow>
              <TableHeaderCell>Name</TableHeaderCell>
              <TableHeaderCell>Email</TableHeaderCell>
              <TableHeaderCell>Last Seen</TableHeaderCell>
              <TableHeaderCell>Actions</TableHeaderCell>
            </TableRow>
          </TableHeader>
          <TableBody>
            {users.map((user) => (
              <TableRow key={user.id}>
                <TableCell>{user.display_name || user.displayName || user.email}</TableCell>
                <TableCell>{user.email}</TableCell>
                <TableCell>
                  {(user.last_sync || user.lastSeen) 
                    ? new Date(user.last_sync || user.lastSeen || "").toLocaleDateString() 
                    : "Never"}
                </TableCell>
                <TableCell>
                  <Button
                    appearance="subtle"
                    icon={<Dismiss24Regular />}
                    onClick={async () => {
                      if (window.confirm(`Delete user ${user.email}?`)) {
                        await fetchApi(`/users/${user.id}`, { method: "DELETE" });
                        loadUsers();
                      }
                    }}
                  >
                    Delete
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
    </div>
  );
};

/**
 * Tools Management Tab
 */
const ToolsTab: React.FC = () => {
  const styles = useStyles();
  const fetchApi = useFetchApi();
  const [tools, setTools] = useState<Tool[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    fetchApi<{ tools: Tool[] }>("/tools")
      .then((res) => setTools(res.tools || []))
      .finally(() => setIsLoading(false));
  }, [fetchApi]);

  if (isLoading) {
    return (
      <div className={styles.loading}>
        <Spinner size="large" />
      </div>
    );
  }

  return (
    <div>
      <Text size={500} weight="semibold" style={{ marginBottom: tokens.spacingVerticalM, display: "block" }}>
        Available Tools ({tools.length})
      </Text>
      <Table>
        <TableHeader>
          <TableRow>
            <TableHeaderCell>Name</TableHeaderCell>
            <TableHeaderCell>Category</TableHeaderCell>
            <TableHeaderCell>Description</TableHeaderCell>
          </TableRow>
        </TableHeader>
        <TableBody>
          {tools.map((tool) => (
            <TableRow key={tool.id}>
              <TableCell>
                <Text weight="semibold">{tool.name}</Text>
              </TableCell>
              <TableCell>{tool.category || "General"}</TableCell>
              <TableCell>{tool.description || "-"}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
};

/**
 * Usage / Audit Log Tab
 */
interface UsageEvent {
  id: string;
  event_type: string;
  timestamp: string;
  user_email: string;
  tool_id?: string;
  subtool_id?: string;
  details?: Record<string, unknown>;
}

interface UsageSummary {
  period_days: number;
  total_events: number;
  by_type: Record<string, number>;
  by_user: Record<string, number>;
  by_tool: Record<string, number>;
  last_logins: Record<string, string>;
}

const EVENT_TYPE_LABELS: Record<string, string> = {
  login: "Login",
  tool_access: "Tool Access",
  subtool_access: "Sub-Tool Access",
  report_run: "Report Run",
  chat_message: "Chat Message",
  report_email: "Report Email",
};

const AuditTab: React.FC = () => {
  const styles = useStyles();
  const fetchApi = useFetchApi();
  const [events, setEvents] = useState<UsageEvent[]>([]);
  const [summary, setSummary] = useState<UsageSummary | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filterType, setFilterType] = useState<string>("");
  const [filterUser, setFilterUser] = useState<string>("");
  const [filterDays, setFilterDays] = useState<number>(30);
  const [showDetail, setShowDetail] = useState(true);

  const loadData = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams();
      params.set("days", String(filterDays));
      params.set("limit", "500");
      if (filterType) params.set("event_type", filterType);
      if (filterUser) params.set("user_email", filterUser);

      const [eventsRes, summaryRes] = await Promise.all([
        fetchApi<{ events: UsageEvent[] }>(`/usage/events?${params.toString()}`),
        fetchApi<UsageSummary>(`/usage/summary?days=${filterDays}`),
      ]);
      setEvents(eventsRes.events || []);
      setSummary(summaryRes);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to load usage data");
    } finally {
      setIsLoading(false);
    }
  }, [fetchApi, filterType, filterUser, filterDays]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const formatTimestamp = (ts: string) => {
    try {
      const d = new Date(ts);
      return d.toLocaleString();
    } catch {
      return ts;
    }
  };

  const formatRelative = (ts: string) => {
    try {
      const d = new Date(ts);
      const now = new Date();
      const diffMs = now.getTime() - d.getTime();
      const diffMins = Math.floor(diffMs / 60000);
      if (diffMins < 1) return "just now";
      if (diffMins < 60) return `${diffMins}m ago`;
      const diffHours = Math.floor(diffMins / 60);
      if (diffHours < 24) return `${diffHours}h ago`;
      const diffDays = Math.floor(diffHours / 24);
      return `${diffDays}d ago`;
    } catch {
      return ts;
    }
  };

  if (isLoading) {
    return (
      <div className={styles.loading}>
        <Spinner size="large" label="Loading usage data..." />
      </div>
    );
  }

  return (
    <div>
      <div className={styles.toolbar}>
        <Text size={500} weight="semibold">
          Usage &amp; Audit Log
        </Text>
        <div style={{ display: "flex", gap: tokens.spacingHorizontalS, alignItems: "center" }}>
          <select
            value={filterDays}
            onChange={(e) => setFilterDays(Number(e.target.value))}
            style={{ padding: "6px 8px", borderRadius: 4, border: `1px solid ${tokens.colorNeutralStroke1}` }}
          >
            <option value={7}>Last 7 days</option>
            <option value={30}>Last 30 days</option>
            <option value={90}>Last 90 days</option>
            <option value={365}>Last year</option>
          </select>
          <select
            value={filterType}
            onChange={(e) => setFilterType(e.target.value)}
            style={{ padding: "6px 8px", borderRadius: 4, border: `1px solid ${tokens.colorNeutralStroke1}` }}
          >
            <option value="">All Events</option>
            <option value="login">Logins</option>
            <option value="tool_access">Tool Access</option>
            <option value="subtool_access">Sub-Tool Access</option>
            <option value="chat_message">Chat Messages</option>
            <option value="report_run">Report Runs</option>
            <option value="report_email">Report Emails</option>
          </select>
          <Input
            placeholder="Filter by email..."
            value={filterUser}
            onChange={(_, data) => setFilterUser(data.value)}
            style={{ width: 200 }}
          />
          <Button appearance="subtle" onClick={() => setShowDetail(!showDetail)}>
            {showDetail ? "Show Summary" : "Show Detail"}
          </Button>
        </div>
      </div>

      {error && <div className={styles.error}>{error}</div>}

      {!showDetail && summary ? (
        <div>
          {/* Summary cards */}
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: 16, marginBottom: 24 }}>
            <Card style={{ padding: 16 }}>
              <Text size={200} style={{ color: tokens.colorNeutralForeground3 }}>Total Events</Text>
              <Text size={700} weight="semibold" block>{summary.total_events}</Text>
              <Text size={100} style={{ color: tokens.colorNeutralForeground3 }}>last {summary.period_days} days</Text>
            </Card>
            <Card style={{ padding: 16 }}>
              <Text size={200} style={{ color: tokens.colorNeutralForeground3 }}>Unique Users</Text>
              <Text size={700} weight="semibold" block>{Object.keys(summary.by_user).length}</Text>
            </Card>
            <Card style={{ padding: 16 }}>
              <Text size={200} style={{ color: tokens.colorNeutralForeground3 }}>Logins</Text>
              <Text size={700} weight="semibold" block>{summary.by_type["login"] || 0}</Text>
            </Card>
            <Card style={{ padding: 16 }}>
              <Text size={200} style={{ color: tokens.colorNeutralForeground3 }}>Tool Accesses</Text>
              <Text size={700} weight="semibold" block>{summary.by_type["tool_access"] || 0}</Text>
            </Card>
          </div>

          {/* Last logins */}
          <Text size={400} weight="semibold" block style={{ marginBottom: 8 }}>Recent Logins</Text>
          <Table size="small" style={{ marginBottom: 24 }}>
            <TableHeader>
              <TableRow>
                <TableHeaderCell>User</TableHeaderCell>
                <TableHeaderCell>Last Login</TableHeaderCell>
                <TableHeaderCell>Total Events</TableHeaderCell>
              </TableRow>
            </TableHeader>
            <TableBody>
              {Object.entries(summary.last_logins).map(([email, ts]) => (
                <TableRow key={email}>
                  <TableCell>{email}</TableCell>
                  <TableCell title={formatTimestamp(ts)}>{formatRelative(ts)}</TableCell>
                  <TableCell>{summary.by_user[email] || 0}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>

          {/* Tool usage */}
          {Object.keys(summary.by_tool).length > 0 && (
            <>
              <Text size={400} weight="semibold" block style={{ marginBottom: 8 }}>Tool Usage</Text>
              <Table size="small">
                <TableHeader>
                  <TableRow>
                    <TableHeaderCell>Tool</TableHeaderCell>
                    <TableHeaderCell>Events</TableHeaderCell>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {Object.entries(summary.by_tool).map(([tool, count]) => (
                    <TableRow key={tool}>
                      <TableCell>{tool}</TableCell>
                      <TableCell>{count}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </>
          )}
        </div>
      ) : (
        <div className={styles.tableContainer}>
          <Text size={200} style={{ color: tokens.colorNeutralForeground3, marginBottom: 8, display: "block" }}>
            Showing {events.length} events
          </Text>
          {events.length === 0 ? (
            <div className={styles.placeholder}>
              <History24Regular style={{ fontSize: 48, marginBottom: 16 }} />
              <Text size={500} weight="semibold">No usage events found</Text>
              <Text>Adjust filters or date range to see events</Text>
            </div>
          ) : (
            <Table size="small">
              <TableHeader>
                <TableRow>
                  <TableHeaderCell style={{ width: 160 }}>Time</TableHeaderCell>
                  <TableHeaderCell style={{ width: 130 }}>Event</TableHeaderCell>
                  <TableHeaderCell>User</TableHeaderCell>
                  <TableHeaderCell>Tool</TableHeaderCell>
                  <TableHeaderCell>Sub-Tool</TableHeaderCell>
                </TableRow>
              </TableHeader>
              <TableBody>
                {events.map((ev) => (
                  <TableRow key={ev.id}>
                    <TableCell title={formatTimestamp(ev.timestamp)}>
                      <Text size={200}>{formatRelative(ev.timestamp)}</Text>
                    </TableCell>
                    <TableCell>
                      <Badge
                        appearance="filled"
                        color={
                          ev.event_type === "login" ? "success"
                          : ev.event_type === "tool_access" ? "brand"
                          : ev.event_type === "chat_message" ? "informative"
                          : "warning"
                        }
                      >
                        {EVENT_TYPE_LABELS[ev.event_type] || ev.event_type}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <Text size={200}>{ev.user_email}</Text>
                    </TableCell>
                    <TableCell>
                      <Text size={200}>{ev.tool_id || "—"}</Text>
                    </TableCell>
                    <TableCell>
                      <Text size={200}>{ev.subtool_id || "—"}</Text>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </div>
      )}
    </div>
  );
};

/**
 * Adoption Tab — who is (and isn't) actually using Photon.
 *
 * Joins the provisioned-user roster (/users) with usage activity
 * (/usage/summary) so it can show the INACTIVE side the Usage Log can't:
 * roster minus anyone active in the window = not using. A focused LERT ×
 * Sales-Assistant cut pulls the CRM tool's per-user events (/usage/events
 * ?tool_id=crm) so "using a sales tool" is distinguished from "just logged in".
 *
 * IMPORTANT: /usage/summary keys users by the email as it appears in the login
 * token, which is NOT lowercased, while the roster stores lowercased email.
 * All joins here are case-insensitive — matching on raw case silently under-
 * counts active users (it moved 11 users, incl. several LERTs, in testing).
 */
const ROLE_LABEL: Record<string, string> = {
  lert: "LERT",
  pm: "Project Manager",
  existing_customer: "Sales Admin",
  sales_admin_lert: "Sales Admin — LERT",
  team_lead: "Team Lead",
};

const AdoptionTab: React.FC = () => {
  const styles = useStyles();
  const fetchApi = useFetchApi();
  const [users, setUsers] = useState<User[]>([]);
  const [summary, setSummary] = useState<UsageSummary | null>(null);
  const [crmEvents, setCrmEvents] = useState<UsageEvent[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filterDays, setFilterDays] = useState<number>(30);

  const loadData = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const [usersRes, summaryRes, crmRes] = await Promise.all([
        fetchApi<{ users: User[] }>(`/users`),
        fetchApi<UsageSummary>(`/usage/summary?days=${filterDays}`),
        fetchApi<{ events: UsageEvent[] }>(`/usage/events?tool_id=crm&days=${filterDays}&limit=1000`),
      ]);
      setUsers(usersRes.users || []);
      setSummary(summaryRes);
      setCrmEvents(crmRes.events || []);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to load adoption data");
    } finally {
      setIsLoading(false);
    }
  }, [fetchApi, filterDays]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  if (isLoading) {
    return (
      <div className={styles.loading}>
        <Spinner size="large" label="Loading adoption data..." />
      </div>
    );
  }

  const lc = (s: string) => (s || "").toLowerCase();
  const byUser: Record<string, number> = {};
  const lastLogin: Record<string, string> = {};
  if (summary) {
    for (const [k, v] of Object.entries(summary.by_user)) byUser[lc(k)] = v;
    for (const [k, v] of Object.entries(summary.last_logins)) lastLogin[lc(k)] = v;
  }
  // Per-user CRM (Sales Assistant) usage + the sub-tab breakdown (populates
  // once CRM subtool tracking has been deployed and accumulates events).
  const crmByUser: Record<string, { count: number; last: string }> = {};
  const crmSubtool: Record<string, number> = {};
  for (const e of crmEvents) {
    const em = lc(e.user_email);
    const c = crmByUser[em] || { count: 0, last: "" };
    c.count += 1;
    if ((e.timestamp || "") > c.last) c.last = e.timestamp;
    crmByUser[em] = c;
    if (e.subtool_id) crmSubtool[e.subtool_id] = (crmSubtool[e.subtool_id] || 0) + 1;
  }

  const roster = users.map((u) => {
    const email = lc(u.email);
    const events = byUser[email] || 0;
    return {
      name: u.display_name || u.displayName || u.email,
      email,
      role: u.tmd_profile || "",
      active: events > 0,
      events,
      lastLogin: lastLogin[email] || "",
      crm: crmByUser[email],
    };
  });
  const provisioned = roster.length;
  const active = roster.filter((r) => r.active).length;
  const inactive = provisioned - active;
  const rate = provisioned ? Math.round((active / provisioned) * 100) : 0;

  const lerts = [...roster]
    .filter((r) => r.role === "lert")
    .sort((a, b) => (b.crm?.count || 0) - (a.crm?.count || 0));
  const lertUsingCrm = lerts.filter((r) => r.crm).length;

  const notUsing = [...roster]
    .filter((r) => !r.active)
    .sort(
      (a, b) =>
        (ROLE_LABEL[b.role] ? 1 : 0) - (ROLE_LABEL[a.role] ? 1 : 0) ||
        a.name.localeCompare(b.name),
    );

  const fmtDate = (ts: string) => (ts ? new Date(ts).toLocaleDateString() : "—");

  const kpi = (label: string, value: React.ReactNode, sub?: string) => (
    <Card style={{ padding: 16 }}>
      <Text size={200} style={{ color: tokens.colorNeutralForeground3 }}>{label}</Text>
      <Text size={700} weight="semibold" block>{value}</Text>
      {sub && <Text size={100} style={{ color: tokens.colorNeutralForeground3 }}>{sub}</Text>}
    </Card>
  );

  return (
    <div>
      <div className={styles.toolbar}>
        <Text size={500} weight="semibold">Adoption</Text>
        <div style={{ display: "flex", gap: tokens.spacingHorizontalS, alignItems: "center" }}>
          <select
            value={filterDays}
            onChange={(e) => setFilterDays(Number(e.target.value))}
            style={{ padding: "6px 8px", borderRadius: 4, border: `1px solid ${tokens.colorNeutralStroke1}` }}
          >
            <option value={7}>Last 7 days</option>
            <option value={30}>Last 30 days</option>
            <option value={90}>Last 90 days</option>
            <option value={365}>Last year</option>
          </select>
        </div>
      </div>

      <Text size={200} style={{ color: tokens.colorNeutralForeground3, display: "block", marginBottom: 16 }}>
        “Using” = any tracked activity (login or tool use) in the last {filterDays} days. “Sales Assistant” = the CRM tool.
      </Text>

      {error && <div className={styles.error}>{error}</div>}

      {/* KPIs */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))", gap: 16, marginBottom: 24 }}>
        {kpi("Provisioned", provisioned, "in the roster")}
        {kpi("Using", active, `active ≤ ${filterDays}d`)}
        {kpi("Not using", inactive, "0 events")}
        {kpi("Adoption", `${rate}%`)}
      </div>

      {/* LERT x Sales Assistant */}
      <Text size={400} weight="semibold" block style={{ marginBottom: 4 }}>
        LERT members — Sales Assistant (CRM)
      </Text>
      <Text size={200} style={{ color: tokens.colorNeutralForeground3, display: "block", marginBottom: 8 }}>
        {lertUsingCrm} of {lerts.length} LERTs used the Sales Assistant in the last {filterDays} days.
      </Text>
      <Table size="small" style={{ marginBottom: 24 }}>
        <TableHeader>
          <TableRow>
            <TableHeaderCell>LERT member</TableHeaderCell>
            <TableHeaderCell>Sales Assistant</TableHeaderCell>
            <TableHeaderCell>CRM events</TableHeaderCell>
            <TableHeaderCell>Last CRM use</TableHeaderCell>
          </TableRow>
        </TableHeader>
        <TableBody>
          {lerts.map((r) => (
            <TableRow key={r.email}>
              <TableCell>{r.name}</TableCell>
              <TableCell>
                {r.crm ? (
                  <Badge appearance="filled" color="success">Using</Badge>
                ) : r.active ? (
                  <Badge appearance="filled" color="warning">Logged in, no CRM</Badge>
                ) : (
                  <Badge appearance="filled" color="danger">No activity</Badge>
                )}
              </TableCell>
              <TableCell>{r.crm?.count || 0}</TableCell>
              <TableCell>{fmtDate(r.crm?.last || "")}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>

      {/* Sales Assistant sub-feature breakdown */}
      <Text size={400} weight="semibold" block style={{ marginBottom: 4 }}>
        Sales Assistant features (CRM sub-tabs)
      </Text>
      {Object.keys(crmSubtool).length === 0 ? (
        <Text size={200} style={{ color: tokens.colorNeutralForeground3, display: "block", marginBottom: 24 }}>
          No sub-tab data yet — CRM sub-tab tracking was recently added; this fills in as events accumulate.
        </Text>
      ) : (
        <Table size="small" style={{ marginBottom: 24 }}>
          <TableHeader>
            <TableRow>
              <TableHeaderCell>Feature</TableHeaderCell>
              <TableHeaderCell>Views</TableHeaderCell>
            </TableRow>
          </TableHeader>
          <TableBody>
            {Object.entries(crmSubtool)
              .sort((a, b) => b[1] - a[1])
              .map(([sub, n]) => (
                <TableRow key={sub}>
                  <TableCell>{sub}</TableCell>
                  <TableCell>{n}</TableCell>
                </TableRow>
              ))}
          </TableBody>
        </Table>
      )}

      {/* Not using */}
      <Text size={400} weight="semibold" block style={{ marginBottom: 4 }}>
        Not using Photon ({inactive})
      </Text>
      <Text size={200} style={{ color: tokens.colorNeutralForeground3, display: "block", marginBottom: 8 }}>
        Provisioned users with zero tracked activity in the last {filterDays} days. Role-holders first.
      </Text>
      <Table size="small">
        <TableHeader>
          <TableRow>
            <TableHeaderCell>Name</TableHeaderCell>
            <TableHeaderCell>Email</TableHeaderCell>
            <TableHeaderCell>Role</TableHeaderCell>
          </TableRow>
        </TableHeader>
        <TableBody>
          {notUsing.map((r) => (
            <TableRow key={r.email}>
              <TableCell>{r.name}</TableCell>
              <TableCell>{r.email}</TableCell>
              <TableCell>
                {ROLE_LABEL[r.role] ? (
                  <Badge appearance="tint" color="danger">{ROLE_LABEL[r.role]}</Badge>
                ) : (
                  <Text size={200} style={{ color: tokens.colorNeutralForeground3 }}>—</Text>
                )}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
};

export const AdminPage: React.FC = () => {
  const styles = useStyles();
  const navigate = useNavigate();
  const location = useLocation();

  // Determine active tab from URL
  const getActiveTab = () => {
    const path = location.pathname.replace("/admin", "") || "/";
    switch (path) {
      case "/users":
        return "users";
      case "/tools":
        return "tools";
      case "/permissions":
        return "permissions";
      case "/audit":
        return "audit";
      case "/adoption":
        return "adoption";
      default:
        return "permissions"; // Default to permissions tab
    }
  };

  const handleTabChange = (_: unknown, data: { value: unknown }) => {
    const tab = String(data.value);
    navigate(`/admin/${tab === "permissions" ? "" : tab}`);
  };

  return (
    <div className={styles.container}>
      {/* Header */}
      <div className={styles.header}>
        <Text size={700} weight="semibold" block style={{ fontFamily: "'Poppins', sans-serif" }}>
          Administration
        </Text>
        <Text style={{ color: tokens.colorNeutralForeground3 }}>
          Manage users, tools, and permissions
        </Text>
      </div>

      {/* Tabs */}
      <div className={styles.tabs}>
        <TabList selectedValue={getActiveTab()} onTabSelect={handleTabChange}>
          <Tab value="permissions" icon={<Shield24Regular />}>
            Permissions
          </Tab>
          <Tab value="users" icon={<People24Regular />}>
            Users
          </Tab>
          <Tab value="tools" icon={<Apps24Regular />}>
            Tools
          </Tab>
          <Tab value="audit" icon={<History24Regular />}>
            Usage Log
          </Tab>
          <Tab value="adoption" icon={<DataUsage24Regular />}>
            Adoption
          </Tab>
        </TabList>
      </div>

      {/* Content */}
      <Card className={styles.content}>
        <Routes>
          <Route path="/" element={<PermissionsTab />} />
          <Route path="/permissions" element={<PermissionsTab />} />
          <Route path="/users" element={<UsersTab />} />
          <Route path="/tools" element={<ToolsTab />} />
          <Route path="/audit" element={<AuditTab />} />
          <Route path="/adoption" element={<AdoptionTab />} />
        </Routes>
      </Card>
    </div>
  );
};

export default AdminPage;
